'use client'

import { useEffect, useState } from 'react'
import { useRouter, usePathname } from 'next/navigation'
import { useUserStore } from '@/hooks/useUser'
import { useAuthSession } from '@/hooks/useAuthActions'
import { Loader2 } from 'lucide-react'

interface AuthGuardProps {
	children: React.ReactNode
}

export function AuthGuard({ children }: Readonly<AuthGuardProps>) {
	const { supabaseUser } = useUserStore()
	const { isInitialized } = useAuthSession()
	const router = useRouter()
	const pathname = usePathname()
	const [isLoading, setIsLoading] = useState(true)

	const isAuthPage = pathname === '/login'
	const isCallbackPage = pathname === '/auth/callback'
	const isProfilePage = pathname === '/profile'
	// A raiz recebe o retorno OAuth via `/?code=` antes de haver sessão no client.
	const isPublicRoute = isAuthPage || isCallbackPage || isProfilePage || pathname === '/'

	useEffect(() => {
		if (isInitialized) {
			setIsLoading(false)

			if (!supabaseUser && !isPublicRoute) {
				// Preserva a rota pretendida no ?redirect= para voltar após login.
				const intended = pathname + (typeof window === 'undefined' ? '' : window.location.search)
				const target = intended && intended !== '/' ? `/login?redirect=${encodeURIComponent(intended)}` : '/login'
				router.push(target)
				return
			}

			if (supabaseUser && isAuthPage) {
				router.push('/')
				return
			}
		}
	}, [supabaseUser, isInitialized, isPublicRoute, isAuthPage, pathname, router])

	// Rota pública não espera a sessão resolver: /login e /auth/callback têm
	// spinner/gate próprios, e segurá-las atrás do isInitialized deixa o usuário
	// preso em "Verificando autenticação..." quando a inicialização trava.
	if (isPublicRoute) {
		return <>{children}</>
	}

	if (!isInitialized || isLoading) {
		return (
			<div className="min-h-screen flex items-center justify-center bg-background">
				<div className="flex flex-col items-center gap-4">
					<Loader2 className="h-8 w-8 animate-spin" />
					<p className="text-muted-foreground">Verificando autenticação...</p>
				</div>
			</div>
		)
	}

	if (supabaseUser) {
		return <>{children}</>
	}

	return (
		<div className="min-h-screen flex items-center justify-center bg-background">
			<div className="flex flex-col items-center gap-4">
				<Loader2 className="h-8 w-8 animate-spin" />
				<p className="text-muted-foreground">Redirecionando...</p>
			</div>
		</div>
	)
}
